import { Pause, Play } from "lucide-react";
import { useCallback, useRef, useState } from "react";

import type { WelcomeVideoTrim } from "@/api/types/outgoingIntake";
import { cn } from "@/lib/utils";

type Props = {
  src: string;
  /** CSS object-fit for the video within its container. Defaults to "cover". */
  objectFit?: "cover" | "contain";
  /**
   * Horizontally mirror the video (selfie view). Display-only; does not alter
   * the underlying file. Used for the attorney's own capture/review, not the
   * client-facing playback.
   */
  mirrored?: boolean;
  /** The part that plays; the file itself is uncut. */
  trim?: WelcomeVideoTrim | null;
  className?: string;
};

/** The seconds of the file that play: `[start, end]`, clamped to it. */
export const trimWindow = (
  trim: WelcomeVideoTrim | null | undefined,
  duration: number
): { start: number; end: number } => {
  const length = Number.isFinite(duration) && duration > 0 ? duration : 0;
  const start = Math.min(Math.max(0, trim?.start ?? 0), length);
  const end =
    trim?.end && trim.end > start
      ? Math.min(trim.end, length || trim.end)
      : length;
  return { start, end: Math.max(start, end) };
};

/** Where Play starts: the trim start, unless already inside the window. */
export const playFrom = (
  current: number,
  span: { start: number; end: number }
): number =>
  current < span.start || (span.end > 0 && current >= span.end - 0.05)
    ? span.start
    : current;

function formatTime(total: number): string {
  if (!Number.isFinite(total) || total < 0) return "0:00";
  const clamped = Math.floor(total);
  const m = Math.floor(clamped / 60);
  const s = clamped % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Typeform-style welcome video: fills its parent and shows a large centered
 * play-button overlay while paused. Play state is local so it never re-renders
 * the host page.
 *
 * When `mirrored`, only the video pixels are flipped (`scaleX(-1)`); the
 * playback controls are rendered by us in the (un-flipped) container so they
 * don't read backwards. The un-mirrored (client-facing) view keeps the browser
 * native controls.
 */
export function WelcomeVideoPlayer({
  src,
  objectFit = "cover",
  mirrored = false,
  trim,
  className
}: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const { start: from, end: until } = trimWindow(trim, duration);
  const trimmed = Boolean(trim && (trim.start || trim.end));
  // Native controls show the whole file; ours show what plays.
  const custom = mirrored || trimmed;

  const start = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const at = playFrom(video.currentTime, { start: from, end: until });
    if (at !== video.currentTime) video.currentTime = at;
    video.play().catch(() => undefined);
  }, [from, until]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      start();
    } else {
      video.pause();
    }
  }, [start]);

  const handleSeek = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const video = videoRef.current;
      if (!video) return;
      const next = Number(event.target.value) + from;
      if (Number.isFinite(next)) {
        video.currentTime = next;
        setCurrentTime(next);
      }
    },
    [from]
  );

  const length = Math.max(0, until - from);
  const elapsed = Math.min(Math.max(0, currentTime - from), length);

  return (
    <div
      className={cn(
        "relative flex h-full w-full items-center justify-center overflow-hidden bg-black",
        className
      )}
    >
      {/* Attorney-recorded welcome clip; no caption track available. */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        src={src}
        playsInline
        preload="metadata"
        controls={playing && !custom}
        className="h-full w-full"
        style={{ objectFit, transform: mirrored ? "scaleX(-1)" : undefined }}
        onClick={custom ? togglePlay : playing ? undefined : start}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onLoadedMetadata={(event) => {
          const video = event.currentTarget;
          setDuration(video.duration);
          const begin = trimWindow(trim, video.duration).start;
          if (begin) video.currentTime = begin;
        }}
        onDurationChange={(event) => setDuration(event.currentTarget.duration)}
        onTimeUpdate={(event) => {
          const video = event.currentTarget;
          setCurrentTime(video.currentTime);
          if (trimmed && until > 0 && video.currentTime >= until) {
            video.pause();
            video.currentTime = from;
          }
        }}
      />
      {!playing ? (
        <button
          type="button"
          aria-label="Play video"
          onClick={start}
          className="absolute inset-0 flex items-center justify-center focus:outline-hidden"
        >
          <span className="flex size-18 items-center justify-center rounded-full bg-elevated/85 shadow-dialog transition-transform duration-200 hover:scale-105">
            <Play className="ml-1 size-7.5 text-fg" aria-hidden />
          </span>
        </button>
      ) : null}

      {/* Custom controls: upright for the mirrored (selfie) view, where native
          ones would render backwards, and timed to the trim when there is one. */}
      {custom && playing ? (
        <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-linear-to-t from-black/70 to-transparent px-4 py-3">
          <button
            type="button"
            aria-label="Pause"
            onClick={togglePlay}
            className="flex size-8 shrink-0 items-center justify-center rounded-full text-white transition-colors hover:bg-white/15 focus:outline-hidden"
          >
            <Pause className="size-5" aria-hidden />
          </button>
          <input
            type="range"
            aria-label="Seek"
            min={0}
            max={length || 0}
            step={0.1}
            value={elapsed}
            onChange={handleSeek}
            disabled={length === 0}
            className="h-1 flex-1 cursor-pointer accent-white"
          />
          <span className="shrink-0 text-sm tabular-nums text-white">
            {formatTime(elapsed)} / {formatTime(length)}
          </span>
        </div>
      ) : null}
    </div>
  );
}

export default WelcomeVideoPlayer;
